import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

/**
 * Blocking modal for legacy users (created before iter60) who lack DOB or
 * accepted the current Terms/Privacy version. Mounted at app root: pops up as
 * soon as `/auth/me` returns `profile_completion_required: true`.
 *
 * The dialog is intentionally non-dismissible — users must either complete
 * the profile or log out to leave. There is no `onOpenChange` handler that
 * closes it on outside click / ESC.
 */
export default function CompleteProfileModal() {
  const { user, refresh, logout } = useAuth();
  const { t } = useI18n();
  const [dob, setDob] = useState("");
  const [acceptAll, setAcceptAll] = useState(false);
  const [busy, setBusy] = useState(false);

  const needsCompletion = !!user && user.profile_completion_required === true;

  useEffect(() => {
    // Reset local state whenever the target user changes (login/logout).
    setDob("");
    setAcceptAll(false);
  }, [user?.user_id]);

  if (!needsCompletion) return null;

  const _today = new Date();
  const _maxDob = _today.toISOString().slice(0, 10);
  const _minDob = new Date(_today.getFullYear() - 120, 0, 1).toISOString().slice(0, 10);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!dob) { toast.error(t("signup_dob_invalid")); return; }
    if (!acceptAll) { toast.error(t("signup_terms_required")); return; }
    setBusy(true);
    try {
      await api.post("/auth/complete-profile", {
        date_of_birth: dob,
        terms_accepted: acceptAll,
        privacy_accepted: acceptAll,
        community_rules_accepted: acceptAll,
      });
      await refresh();
      toast.success(t("profile_updated") || "OK");
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (detail === "age_below_minimum_account_removed") {
        // Backend removed the account. Force logout + local cleanup so the
        // user lands back on the public landing page with no lingering state.
        toast.error(t("signup_age_required"));
        try { localStorage.removeItem("tc_token"); } catch {}
        await logout();
        window.location.href = "/";
        return;
      }
      if (detail === "invalid_date_of_birth") { toast.error(t("signup_dob_invalid")); }
      else if (detail === "terms_and_privacy_required") { toast.error(t("signup_terms_required")); }
      else { toast.error(detail || t("generic_error")); }
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={() => { /* non-dismissible */ }}>
      <DialogContent
        data-testid="complete-profile-modal"
        className="max-w-md"
        // Prevent the default shadcn "click outside to close" and "esc to close".
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-emerald-700" />
            {t("complete_profile_title")}
          </DialogTitle>
          <DialogDescription>{t("complete_profile_desc")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label htmlFor="cp-dob" className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
              {t("signup_dob_label")}
            </Label>
            <Input
              id="cp-dob"
              data-testid="cp-dob"
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              min={_minDob}
              max={_maxDob}
              required
              className="mt-1.5 h-11 rounded-xl"
            />
            <p className="text-[11px] text-slate-500 mt-1">{t("signup_dob_help")}</p>
          </div>
          <div className="space-y-2">
            <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                data-testid="cp-accept-all"
                checked={acceptAll}
                onChange={(e) => setAcceptAll(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              />
              <span>
                {t("signup_consent_prefix")}{" "}
                <Link to="/terms" target="_blank" className="text-emerald-700 font-semibold underline">
                  {t("signup_terms_link")}
                </Link>{" "}
                {t("signup_consent_and_the")}{" "}
                <Link to="/community-rules" target="_blank" className="text-emerald-700 font-semibold underline">
                  {t("signup_community_link")}
                </Link>{" "}
                {t("signup_consent_privacy_note")}{" "}
                <Link to="/privacy" target="_blank" className="text-emerald-700 font-semibold underline">
                  {t("signup_privacy_link")}
                </Link>
                .
              </span>
            </label>
          </div>
          <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
            {t("complete_profile_age_warning")}
          </p>
          <div className="flex gap-2 pt-1">
            <Button
              type="submit"
              data-testid="cp-submit"
              disabled={busy}
              className="flex-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white hover:text-white h-11"
            >
              {busy ? "..." : t("complete_profile_cta")}
            </Button>
            <Button
              type="button"
              data-testid="cp-logout"
              variant="outline"
              onClick={async () => { await logout(); window.location.href = "/"; }}
              className="rounded-full h-11"
            >
              {t("logout_label") || t("logout") || "Logout"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
